import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { ActionGroup } from '../../../../elements/components/action-group/entities/action-group.entity';
import { ShFormCheckboxWithDropdownModule } from '../../../../form-controls/components/form-checkbox-with-dropdown/form-checkbox-with-dropdown.module';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../../form-controls/entities/form-value.entity';
import { Comparable, Trackable } from '../../../../helpers/public_api';
import { ColumnDef } from '../../../../overviews/list-view/entities/column-definition.entity';
import { ColumnSelectionComponent } from '../column-selection/column-selection.component';
import { ColumnSortComponent } from '../column-sort/column-sort.component';
import { ShContextActionBarComponent } from '../context-action-bar/context-action-bar.component';
import { ActionMode, SelectionState } from '../context-action-bar/entities/select-mode.entity';
import { SortInfo } from '../display-settings-bar/entities/sort-info.entity';

@Component({
    standalone: true,
    selector: 'sh-table-head',
    templateUrl: './table-head.component.html',
    styleUrls: ['./table-head.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShContextActionBarComponent,
        ShFormCheckboxWithDropdownModule,
        TranslateModule,
        ColumnSelectionComponent,
        ColumnSortComponent
    ]
})
export class ShTableHeadComponent<T extends Trackable & Comparable> extends BaseContainer implements AfterViewInit {
    public actionModes: FormSelectOption<ActionMode>[] = [
        { value: ActionMode.BULK, label: this.translateService.instant('general.select-mode.bulk') },
        { value: ActionMode.BATCH, label: this.translateService.instant('general.select-mode.batch') }
    ];

    public defaultSelectControl: UntypedFormControl = new UntypedFormControl();
    public extendedSelectControl: UntypedFormControl = new UntypedFormControl();

    // #region Setup
    @Input() columns: ColumnDef<any, T>[] = [];
    @Input() columnOptions: FormSelectOption[] = [];
    @Input() availableSelect: boolean;
    @Input() availableDragAndDrop: boolean = false;
    @Input() availableActionItems: boolean;

    @Input() contextActionGroup: ActionGroup;

    @Input() itemsSelectedLabelKey: string;
    @Input() noItemsSelectedLabelKey: string;
    // #endregion

    // #region Computational inputs
    @Input() canBatchSelect: boolean;
    @Input() selectedItemsCount: number = 0;
    @Input() selectedPageItemsCount: number;
    @Input() totalPageItemsCount: number;

    @Input() isInitialBulk: boolean;
    @Input() hideColumnHeaders: boolean;

    @Input() allowSort: boolean = true;
    @Input() currentSort: SortInfo;
    // #endregion

    // #region Emitting events
    @Output() selectionChanged = new EventEmitter<SelectionState>();
    @Output() sortInfoChanged = new EventEmitter<SortInfo>();
    @Output() columnSelectionChanged = new EventEmitter<string[]>();
    // #endregion

    ngAfterViewInit(): void {
        // doesn't work when put in onInit for some reason
        this.extendedSelectControl.setValue(this.getBaseSelectState(ActionMode.BULK), { emitEvent: false });
        this.initSelectControlListener();
    }

    public initSelectControlListener(): void {
        let previousValue: any | null = null;

        this.extendedSelectControl.valueChanges
            .pipe(skip(1), distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(value => {
                const hasPreviousValue: boolean = !!previousValue;

                const checkbox = value.checkbox.value;
                const actionMode: ActionMode = value.selection.find(
                    (actionMode: FormValue<ActionMode>) => actionMode.state === FormValueState.SET
                )?.value;

                if (hasPreviousValue) {
                    const previousCheckbox = previousValue.checkbox.value;
                    const previousActionMode: ActionMode = previousValue.selection.find(
                        (actionMode: FormValue<ActionMode>) => actionMode.state === FormValueState.SET
                    )?.value;

                    if (isEqual(checkbox, previousCheckbox) && isEqual(actionMode, previousActionMode)) {
                        this.extendedSelectControl.setValue(this.getBaseSelectState(actionMode), { emitEvent: false });
                        this.onSelectionChanged({ selectAll: true, deselectAll: false, actionMode });
                    }
                }

                previousValue = value;
                if (actionMode === ActionMode.BATCH || actionMode === ActionMode.BULK) {
                    this.extendedSelectControl.setValue(this.getBaseSelectState(actionMode), { emitEvent: false });
                    this.onSelectionChanged({ selectAll: true, deselectAll: false, actionMode });
                    return;
                }
            });
    }

    public getBaseSelectState(actionMode: ActionMode): any {
        return {
            checkbox: new FormValue(false, FormValueState.SET),
            selection: [new FormValue(actionMode, FormValueState.SET)]
        };
    }

    // #region Events
    public onSelectionChanged(selectionState: SelectionState): void {
        this.defaultSelectControl.setValue(false, { emitEvent: false });
        this.selectionChanged.emit(selectionState);
    }

    public onColumnSelectionChanged(selectedColumns: string[]): void {
        this.columnSelectionChanged.emit(selectedColumns);
    }

    public onSimpleSelectionChanged(): void {
        this.defaultSelectControl.setValue(false, { emitEvent: false });
        this.selectionChanged.next({ selectAll: true, deselectAll: false, actionMode: ActionMode.BULK });
    }

    protected onSortInfoChanged(sortInfo: SortInfo): void {
        this.sortInfoChanged.next(sortInfo);
    }
    // #endregion
}
